import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const filteredArticles = articles.filter((a) => a.category === currentCategory.slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const handleCategoryClick = (e: React.MouseEvent, catSlug: string) => {
    e.preventDefault();
    window.REACT_APP_NAVIGATE(`/category/${catSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 正太漫画`,
    description: currentCategory.description,
    url: typeof window !== 'undefined' ? window.location.href : '',
    isPartOf: {
      '@type': 'WebSite',
      name: '正太漫画',
      url: typeof window !== 'undefined' ? window.location.origin : '',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: currentCategory.name,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory.name },
              ]}
            />

            {/* Category Header */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className={`${currentCategory.icon} text-lg`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl">
                {currentCategory.description}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={`/category/${cat.slug}`}
                  onClick={(e) => handleCategoryClick(e, cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                    cat.slug === currentCategory.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </a>
              ))}
            </div>

            {/* Articles Grid */}
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.slug} {...article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
                <Link
                  to="/"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-block mt-3 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                >
                  返回首页
                </Link>
              </div>
            )}

            {/* Related Categories */}
            <div className="mt-14 pt-10 border-t border-background-200/70">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">探索其他分类</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {categories
                  .filter((c) => c.slug !== currentCategory.slug)
                  .map((cat) => (
                    <a
                      key={cat.slug}
                      href={`/category/${cat.slug}`}
                      onClick={(e) => handleCategoryClick(e, cat.slug)}
                      className="flex items-center gap-3 p-4 rounded-xl bg-background-100 hover:bg-background-200 transition-colors border border-background-200/70 cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                        <i className={`${cat.icon} text-sm`}></i>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                        <p className="text-xs text-foreground-500 line-clamp-1">{cat.description}</p>
                      </div>
                    </a>
                  ))}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}